import ImageUploadField from "../ImageUploadField";

import React, { useEffect, useState } from "react";
import {
    Form,
    Input,
    InputNumber,
    DatePicker,
    Switch,
    Select,
    Button,
    Row,
    Col,
    Modal,
    App,
} from "antd";
import API from "API";
import { formatVND } from "general_function";
const { RangePicker } = DatePicker;
const { Option } = Select;

const typeField = {
    upload: (props) => <ImageUploadField {...props} />,
    input: (props) => <Input {...props} />,
    inputNumber: (props) => (
        <InputNumber style={{ width: "100%" }} {...props} />
    ),
    textarea: (props) => <Input.TextArea {...props} rows={4} />,
    select: (props, options) => (
        <Select {...props}>
            {options?.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                    {opt.label}
                </Option>
            ))}
        </Select>
    ),
    switch: (props) => <Switch {...props} />,
    date: (props) => <DatePicker style={{ width: "100%" }} {...props} />,
    dateTime: (props) => <DatePicker style={{ width: "100%" }} showTime {...props} />,
    rangeTime: (props) => <RangePicker style={{ width: "100%" }} {...props} />,
    vnd: (props) => {
    return <InputNumber
        suffix="₫"
        defaultValue={0}
        step={10000}
        formatter={(value)=>formatVND(value)}
        parser={(value)=>value.replaceAll(".","")}
        style={{ ...props.style, width: "100%"}}
        {...props}
    />}
};

const BaseForm = ({
    layout = [],
    initialValues = {},
    apiPath = "",
    itemName = "mục",
    submitLabel = "Lưu",
    loading = false,
    visible,
    onClose,
    onSuccess,
    additionalPostData = {},
    patchMode = true, // dùng PATCH mặc định
    dateFields = [],
    onSubmitError = null,
}) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (initialValues && Object.keys(initialValues).length > 0) {
            // Chuyển credit về number nếu tồn tại
            const sanitized = {
                ...initialValues,
            };
            form.setFieldsValue(initialValues);
        } else {
            form.resetFields();
        }
    }, [initialValues, form]);

    const isFileValue = (val) =>
        val instanceof File ||
        (Array.isArray(val) && val[0]?.originFileObj instanceof File);

    const buildPayload = (values) => {
        const hasFile = Object.values(values).some(isFileValue);
        const finalData = { ...values, ...additionalPostData };

        if (!hasFile) {
            // Nếu là update và logo là URL thì xóa key "logo"
            if (
                "logo" in finalData &&
                typeof finalData.logo === "string" &&
                finalData.logo.startsWith("http")
            ) {
                delete finalData.logo;
            }
            return finalData;
        }

        if (typeof finalData.credit === "string") {
            finalData.credit = Number(finalData.credit);
        }

        const formData = new FormData();
        for (const key in finalData) {
            const val = finalData[key];

            if (val instanceof File) {
                formData.append(key, val);
            } else if (Array.isArray(val) && val[0]?.originFileObj) {
                formData.append(key, val[0].originFileObj);
            } else if (
                key === "logo" &&
                typeof val === "string" &&
                val.startsWith("http")
            ) {
                // Bỏ qua logo nếu là URL cũ (không phải file mới được chọn)
                continue;
            } else if (val !== undefined && val !== null) {
                formData.append(key, val);
            }
        }
        return formData;
    };

    const handleFinish = async (values) => {
        try {
            if (!apiPath) {
                console.warn("Thiếu apiPath trong BaseForm");
                return;
            }

            const isUpdate = initialValues && initialValues.id;
            const mergedValues = isUpdate
                ? { ...initialValues, ...values }
                : { ...values, ...additionalPostData };
            const payload = buildPayload(mergedValues);
            const isMultipart = payload instanceof FormData;

            const config = isMultipart
                ? { headers: { "Content-Type": "multipart/form-data" } }
                : {};
            for (const f of dateFields) {
                payload[f.field] = payload[f.field] && (f.type === "dateTime" 
                ? payload[f.field].format("YYYY-MM-DD HH:mm:ss")
                : f.type === "date" ? payload[f.field].format("YYYY-MM-DD")
                : payload[f.field])
            }
            setSubmitting(true);
            if (isUpdate) {
                const method = patchMode ? "patch" : "put";
                await API[method](
                    `${apiPath}${initialValues.id}/`,
                    payload,
                    config
                );
                message.success(`Cập nhật ${itemName} thành công!`);
            } else {
                const response = await API.post(apiPath, payload, config);
                message.success(`Thêm ${itemName} thành công!`);
            }

            if (onSuccess) onSuccess();
        } catch (error) {
            onSubmitError && onSubmitError(error);
            if (error.response) {
                console.error(
                    "Response error:",
                    error.response.status,
                    error.response.data
                );
            } else if (error.request) {
                console.error("No response received:", error.request);
            } else {
                console.error("Error setting up request:", error.message);
            }
            message.error(`Lỗi khi lưu ${itemName}.`);
        } finally {
            setSubmitting(false);
        }
    };

    const content = (
        <Form form={form} layout="vertical" onFinish={handleFinish}>
            {layout.map((row, rowIndex) => (
                <Row gutter={16} key={rowIndex}>
                    {row.map((field) => {
                        const {
                            name,
                            label,
                            type = "input",
                            span,
                            options = [],
                            rules = [],
                            ...rest
                        } = field;
                        const renderInput = typeField[type] || typeField.input;
                        const computedSpan =
                            span ?? Math.floor(24 / row.length);
                        return (
                            <Col span={computedSpan} key={name}>
                                <Form.Item
                                    name={name}
                                    label={label}
                                    rules={rules}
                                    valuePropName={
                                        type === "upload" ? "value" : undefined
                                    }
                                >
                                    {renderInput(rest, options)}
                                </Form.Item>
                            </Col>
                        );
                    })}
                </Row>
            ))}
            <Form.Item style={{ textAlign: "right" }}>
                <Button type="primary" htmlType="submit" loading={submitting || loading} disabled={submitting || loading}>
                    {submitLabel}
                </Button>
            </Form.Item>
        </Form>
    );

    return typeof visible === "boolean" ? (
        <Modal open={visible} onCancel={onClose} footer={null} destroyOnClose>
            {content}
        </Modal>
    ) : (
        content
    );
};

export default BaseForm;
